import {
  type RemixiconComponentType,
  RiForbidLine,
  RiQuestionLine,
} from '@remixicon/react';

import { StateIcon, StateIconProps } from '@spotify/confidence-core-react';
import { ObservationsV1IssueState as IssueState } from '@spotify/confidence-graphql';

import { IssueCloseReason, getIssueStateIconState } from '../../types';

const ICON_SIZE = 16;

export type IssueStateIconProps = Pick<
  StateIconProps,
  'aria-label' | 'iconProps' | 'id' | 'label' | 'typographyProps'
> & {
  closeReason?: IssueCloseReason | null;
  disableIcon?: boolean;
  isAgentRunning?: boolean;
  isSnoozed?: boolean;
  state: IssueState;
};

const getCloseReasonIcon = (
  closeReason: IssueCloseReason | null | undefined,
): RemixiconComponentType | null => {
  switch (closeReason) {
    case IssueCloseReason.WontFix:
      return RiForbidLine;
    case IssueCloseReason.NonIssue:
      return RiQuestionLine;
    default:
      return null;
  }
};

export const IssueStateIcon = ({
  'aria-label': ariaLabel,
  closeReason,
  disableIcon = false,
  id,
  iconProps,
  isAgentRunning = false,
  isSnoozed = false,
  label,
  state,
  typographyProps,
}: IssueStateIconProps) => {
  const CloseReasonIcon =
    state === IssueState.Closed && !isSnoozed && !isAgentRunning
      ? getCloseReasonIcon(closeReason)
      : null;
  const iconSize = iconProps?.style?.fontSize ?? ICON_SIZE;

  if (disableIcon) {
    return (
      <StateIcon.Root aria-label={ariaLabel} id={id}>
        {label && (
          <StateIcon.Label {...typographyProps}>{label}</StateIcon.Label>
        )}
      </StateIcon.Root>
    );
  }

  if (!CloseReasonIcon) {
    return (
      <StateIcon
        aria-label={ariaLabel}
        id={id}
        state={
          isAgentRunning
            ? 'transition'
            : getIssueStateIconState(isSnoozed ? IssueState.Closed : state)
        }
        label={label}
        typographyProps={typographyProps}
        iconProps={{
          ...iconProps,
          style: { fontSize: ICON_SIZE, ...iconProps?.style },
        }}
      />
    );
  }

  return (
    <StateIcon.Root aria-label={ariaLabel} id={id}>
      <CloseReasonIcon size={iconSize} aria-hidden />
      {label && <StateIcon.Label {...typographyProps}>{label}</StateIcon.Label>}
    </StateIcon.Root>
  );
};
